import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Icon, IconName } from '@hills-design-system/components';
import { CmsFile } from '../../types/contentstack';

interface ContentAccordionItem {
  accordion_title: string;
  accordion_text: string;
  image?: CmsFile;
}

interface CmsContentAccordionData {
  name?: string;
  topic_heading?: string;
  sub_heading?: string;
  items: ContentAccordionItem[];
  bgColor?: string;
  component?: string;
  top_padding?: boolean;
}

interface CmsContentAccordionProps {
  data: CmsContentAccordionData;
  className?: string;
}

const SLIDESHOW_INTERVAL = 5000;
const TRANSITION_DURATION = 300;
const PROGRESS_UPDATE_INTERVAL = 100;
const PROGRESS_INCREMENT = 100 / (SLIDESHOW_INTERVAL / PROGRESS_UPDATE_INTERVAL);

export const CmsContentAccordion: React.FC<CmsContentAccordionProps> = ({
  data,
  className = '',
}) => {
  const [openPanels, setOpenPanels] = useState<Set<string>>(new Set());
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isAccordionsContentAccordion = data.component === 'AccordionsContentAccordion';
  const hasStyledLayout = isAccordionsContentAccordion && data.top_padding === true;

  const imagesWithData = useMemo(
    () => data.items?.filter((item) => item.image?.url) ?? [],
    [data.items]
  );

  const hasImages = imagesWithData.length > 0;

  const togglePanel = useCallback((title: string) => {
    setOpenPanels((prev) => {
      const next = new Set<string>();
      if (!prev.has(title)) {
        next.add(title);
      }
      return next;
    });
  }, []);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  const handleSlideChange = useCallback((index: number) => {
    setCurrentSlide(index);
    setProgress(0);
  }, []);

  const controlId = useCallback(
    (title: string) => title.replace(/[^\w\s]| /gi, '_').toLowerCase() + '_panel',
    []
  );

  const addExternalLinkIcons = useCallback((html: string) => {
    const externalIconSvg = renderToStaticMarkup(
      <span className="inline-block align-middle ml-1.5 relative -top-0.5">
        <Icon icon={IconName.ExternalLink} size={20} color="#0054A4" />
      </span>
    );
    return html.replace(/<\/a>/g, externalIconSvg + '</a>');
  }, []);

  useEffect(() => {
    imagesWithData.forEach((item) => {
      if (item.image?.url) {
        const img = new Image();
        img.src = item.image.url;
      }
    });
  }, [imagesWithData]);

  const advanceSlide = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % imagesWithData.length);
      setProgress(0);
      setIsTransitioning(false);
    }, TRANSITION_DURATION);
  }, [imagesWithData.length]);

  const updateProgress = useCallback(() => {
    setProgress((prev) => (prev >= 100 ? 0 : prev + PROGRESS_INCREMENT));
  }, []);

  useEffect(() => {
    const shouldAutoplay = openPanels.size === 0 && !isPaused && imagesWithData.length > 1;

    if (shouldAutoplay) {
      setProgress(0);
      intervalRef.current = setInterval(advanceSlide, SLIDESHOW_INTERVAL);
      progressIntervalRef.current = setInterval(updateProgress, PROGRESS_UPDATE_INTERVAL);
    } else {
      setProgress(0);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [openPanels.size, isPaused, imagesWithData.length, advanceSlide, updateProgress]);

  const openItem = useMemo(() => {
    if (openPanels.size === 0) return null;
    const openTitle = Array.from(openPanels)[0];
    return data.items?.find((item) => item.accordion_title === openTitle);
  }, [openPanels, data.items]);

  const currentImage = useMemo(() => {
    if (imagesWithData.length === 0) return null;
    return imagesWithData[currentSlide];
  }, [imagesWithData, currentSlide]);

  const accordionItems = (
    <div className={`w-full ${hasImages ? 'lg:w-[519px]' : 'flex-1'} space-y-0`}>
      {data.items?.map((item) => {
        const isOpen = openPanels.has(item.accordion_title);
        const panelId = controlId(item.accordion_title);

        return (
          <div
            key={item.accordion_title}
            className={
              isAccordionsContentAccordion
                ? 'border-b border-gray-300'
                : `border-b-2 border-[#B1CAE4] ${data.items?.indexOf(item) === 0 ? 'border-t-2' : ''}`
            }
          >
            <button
              type="button"
              onClick={() => togglePanel(item.accordion_title)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className={`w-full flex items-center justify-between ${isAccordionsContentAccordion ? 'py-6' : 'py-250'} px-0 text-left transition-colors group`}
              style={
                isAccordionsContentAccordion
                  ? undefined
                  : isOpen
                    ? { paddingBottom: '24px' }
                    : undefined
              }
            >
              <span
                className={`font-bold ${isAccordionsContentAccordion ? 'text-xl leading-tight text-gray-900 group-hover:text-gray-700 pr-4' : 'text-base leading-[1.38] text-[#003672] group-hover:text-blue-800'}`}
              >
                {item.accordion_title}
              </span>
              <span
                className={`transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              >
                <Icon
                  icon={IconName.ChevronDown}
                  size={14}
                  className={
                    isAccordionsContentAccordion ? 'text-black/80' : 'text-[#003672]'
                  }
                />
              </span>
            </button>

            {isOpen && (
              <section
                id={panelId}
                aria-labelledby={`${panelId}-button`}
                className={isAccordionsContentAccordion ? 'pb-6 px-0' : 'pb-250 px-0'}
              >
                <div
                  className="cms-accordion-rich-text text-gray-700 prose prose-base max-w-none prose-p:!max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: addExternalLinkIcons(item.accordion_text),
                  }}
                />
              </section>
            )}
          </div>
        );
      })}
    </div>
  );

  if (hasStyledLayout) {
    return (
      <div
        className={`flex flex-col items-center w-full py-800 px-500 ${className}`}
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="flex justify-between items-start self-stretch w-full">
          {data.name && (
            <h2
              className="font-bold text-heading-300 leading-heading tracking-[-1.44px] text-gray-925 m-0 flex-1 max-w-[455px]"
              style={{ fontFeatureSettings: "'liga' off" }}
            >
              {data.name}
            </h2>
          )}
          <div className="w-[676px] flex-shrink-0 space-y-0">
            {data.items?.map((item) => {
              const isOpen = openPanels.has(item.accordion_title);
              const panelId = controlId(item.accordion_title);

              return (
                <div key={item.accordion_title} className="border-b border-gray-300">
                  <button
                    type="button"
                    onClick={() => togglePanel(item.accordion_title)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="w-full flex items-center justify-between py-6 px-0 text-left transition-colors group"
                  >
                    <span className="font-bold text-xl leading-tight text-gray-900 group-hover:text-gray-700 pr-4">
                      {item.accordion_title}
                    </span>
                    <span
                      className={`transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    >
                      <Icon icon={IconName.ChevronDown} size={14} className="text-black/80" />
                    </span>
                  </button>

                  {isOpen && (
                    <section
                      id={panelId}
                      aria-labelledby={`${panelId}-button`}
                      className="pb-6 px-0"
                    >
                      <div
                        className="cms-accordion-rich-text text-gray-700 prose prose-base max-w-none prose-p:!max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: addExternalLinkIcons(item.accordion_text),
                        }}
                      />
                    </section>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${isAccordionsContentAccordion ? '' : `w-full ${hasImages ? 'pt-800 pb-500 px-6' : 'mx-500 mb-500 rounded-[10px] px-[40px] pb-[24px] pt-[40px]'}`} ${className}`}
      style={
        isAccordionsContentAccordion
          ? {}
          : hasImages
            ? { backgroundColor: data.bgColor ?? '#E8EEF5' }
            : { backgroundColor: '#EDF2F8', width: 'calc(100% - 160px)' }
      }
    >
      <div className={hasImages ? 'mx-auto max-w-7xl' : ''}>
        {data.topic_heading && (
          <h2
            className={
              hasImages
                ? 'text-center mb-500 font-bold text-heading-200 leading-heading tracking-[-0.03em] text-black/90'
                : 'font-bold text-heading-300 pb-[24px] leading-[112.5%] tracking-[-1.44px] text-black mb-0'
            }
          >
            {data.topic_heading}
          </h2>
        )}

        {data.sub_heading && (
          <div
            className={
              hasImages
                ? 'text-center text-lg leading-7 mb-12 text-gray-700'
                : 'text-body-100 leading-body text-gray-700 mt-50'
            }
          >
            {data.sub_heading}
          </div>
        )}

        <div className={`flex flex-col ${hasImages ? 'lg:flex-row gap-12' : ''} items-start`}>
          {accordionItems}

          {hasImages && (
            <div className="w-full lg:w-[50%] lg:sticky lg:top-24 lg:self-start">
              {openItem?.image?.url ? (
                <img
                  key={openItem.accordion_title}
                  src={openItem.image.url}
                  alt={openItem.image.description ?? openItem.accordion_title}
                  className="w-full aspect-[628/862] rounded-2xl object-cover"
                />
              ) : (
                currentImage && (
                  <div className="relative w-full aspect-[628/862] rounded-2xl overflow-hidden">
                    <img
                      key={currentSlide}
                      src={currentImage.image!.url}
                      alt={currentImage.image!.description ?? currentImage.accordion_title}
                      className={`w-full h-full object-cover transition-opacity duration-300 ${
                        isTransitioning ? 'opacity-0' : 'opacity-100'
                      }`}
                    />
                    <button
                      onClick={togglePause}
                      className="absolute bottom-6 right-6 bg-white hover:bg-gray-50 p-4 rounded-full shadow-xl transition-all"
                      aria-label={isPaused ? 'Play slideshow' : 'Pause slideshow'}
                    >
                      {isPaused ? (
                        <svg
                          className="w-6 h-6 text-gray-900"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      ) : (
                        <svg
                          className="w-6 h-6 text-gray-900"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M5.75 3a.75.75 0 00-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 00.75-.75V3.75A.75.75 0 007.25 3h-1.5zM12.75 3a.75.75 0 00-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 00.75-.75V3.75a.75.75 0 00-.75-.75h-1.5z" />
                        </svg>
                      )}
                    </button>
                    {imagesWithData.length > 1 && (
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                        {imagesWithData.map((item, index) => (
                          <button
                            key={`slide-${item.image?.url ?? index}`}
                            onClick={() => handleSlideChange(index)}
                            className={`h-2 rounded-full transition-all ${
                              index === currentSlide ? 'bg-blue-600 w-8' : 'bg-white/70 w-2'
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-white/30">
                      <div
                        className="h-full bg-blue-600 transition-all duration-100 ease-linear"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CmsContentAccordion;
